import React, { useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
    Node,
    Edge,
    Controls,
    Background,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    ConnectionLineType,
    Panel,
    useReactFlow,
    ReactFlowProvider,
} from 'reactflow';
import dagre from 'dagre';
import { User } from '../../types/user';
import { useTheme } from '../../contexts/ThemeContext';
import OrgChartNode from './OrgChartNode';

import 'reactflow/dist/style.css';

interface OrganizationChartProps {
    users: User[];
    managerId?: number | null;
}

interface OrgNode {
    user: User;
    subordinates: OrgNode[];
}

const nodeTypes = {
    orgNode: OrgChartNode,
};

// Dagre graph setup
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 200;
const nodeHeight = 150;

const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'TB') => {
    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({
        rankdir: direction,
        nodesep: 80,
        ranksep: 120,
    });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const newNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            targetPosition: isHorizontal ? 'left' : 'top',
            sourcePosition: isHorizontal ? 'right' : 'bottom',
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2,
            },
        } as Node;
    });

    return { nodes: newNodes, edges };
};

const OrganizationChartInner: React.FC<OrganizationChartProps> = ({ users, managerId }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { fitView } = useReactFlow();

    // Build the tree structure
    const tree = useMemo(() => {
        if (!users.length) return null;

        const userMap = new Map<number, OrgNode>();
        users.forEach(u => userMap.set(u.id, { user: u, subordinates: [] }));

        const roots: OrgNode[] = [];

        // Helper function to detect cycles in the manager hierarchy
        const hasCycle = (userId: number, managerId: number, visited = new Set<number>()): boolean => {
            if (userId === managerId) return true; // Self-reference
            if (visited.has(managerId)) return true; // Already visited

            visited.add(managerId);

            const manager = users.find(u => u.id === managerId);
            if (manager?.manager_id && manager.manager_id !== managerId) {
                return hasCycle(userId, manager.manager_id, visited);
            }

            return false;
        };

        users.forEach(u => {
            const node = userMap.get(u.id)!;

            // Check if adding this user would create a cycle
            if (u.manager_id && userMap.has(u.manager_id)) {
                // Verify no cycle exists before adding to subordinates
                if (!hasCycle(u.id, u.manager_id)) {
                    userMap.get(u.manager_id)!.subordinates.push(node);
                } else {
                    console.warn(`Cycle detected: User ${u.id} (${u.full_name}) has circular manager reference`);
                    roots.push(node); // Treat as root if cycle detected
                }
            } else {
                roots.push(node);
            }
        });

        if (managerId) {
            const managerNode = userMap.get(managerId);
            if (managerNode) return [managerNode];
        }

        return roots;
    }, [users, managerId]);

    // Convert tree to React Flow nodes and edges
    const { initialNodes, initialEdges } = useMemo(() => {
        if (!tree || tree.length === 0) {
            return { initialNodes: [], initialEdges: [] };
        }

        const nodes: Node[] = [];
        const edges: Edge[] = [];
        const visited = new Set<number>();

        const traverse = (orgNode: OrgNode, isRoot: boolean = false) => {
            const userId = orgNode.user.id;

            // Prevent infinite recursion by checking if we've already visited this node
            if (visited.has(userId)) {
                console.warn(`Circular reference detected for user ${userId} (${orgNode.user.full_name})`);
                return;
            }

            visited.add(userId);

            // Add node
            nodes.push({
                id: String(userId),
                type: 'orgNode',
                data: {
                    user: orgNode.user,
                    isRoot,
                },
                position: { x: 0, y: 0 }, // Will be set by dagre
            });

            // Add edges and traverse children
            orgNode.subordinates.forEach(sub => {
                const subUserId = sub.user.id;

                // Only add edge if we haven't visited the target node yet
                if (!visited.has(subUserId)) {
                    edges.push({
                        id: `e${userId}-${subUserId}`,
                        source: String(userId),
                        target: String(subUserId),
                        type: ConnectionLineType.SmoothStep,
                        animated: false,
                        style: {
                            stroke: isDark ? '#4b5563' : '#d1d5db',
                            strokeWidth: 2,
                        },
                    });
                    traverse(sub, false);
                } else {
                    console.warn(`Skipping circular edge from ${userId} to ${subUserId}`);
                }
            });
        };

        tree.forEach(root => traverse(root, true));

        return { initialNodes: nodes, initialEdges: edges };
    }, [tree, isDark]);

    // Apply dagre layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
        if (initialNodes.length === 0) {
            return { nodes: [], edges: [] };
        }
        return getLayoutedElements(initialNodes, initialEdges, 'TB');
    }, [initialNodes, initialEdges]);

    const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

    // Update nodes when layout changes
    React.useEffect(() => {
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
    }, [layoutedNodes, layoutedEdges, setNodes, setEdges]);

    // Center and fit the view when nodes are loaded or updated
    useEffect(() => {
        if (nodes.length > 0) {
            // Small delay to ensure nodes are rendered before fitting
            const timer = setTimeout(() => {
                fitView({ padding: 0.2, maxZoom: 1, duration: 200 });
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [nodes, fitView]);

    const onLayout = useCallback(
        (direction: 'TB' | 'LR') => {
            const { nodes: newNodes, edges: newEdges } = getLayoutedElements(
                nodes,
                edges,
                direction,
            );
            setNodes([...newNodes]);
            setEdges([...newEdges]);
            // Fit view after layout change
            setTimeout(() => {
                fitView({ padding: 0.2, maxZoom: 1, duration: 200 });
            }, 50);
        },
        [nodes, edges, setNodes, setEdges, fitView],
    );

    if (!users.length) {
        return (
            <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No members in this department
            </div>
        );
    }

    return (
        <div
            className={`rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
            style={{ height: '600px' }}
        >
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                connectionLineType={ConnectionLineType.SmoothStep}
                fitView
                fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
                minZoom={0.1}
                maxZoom={1.5}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={true}
                proOptions={{ hideAttribution: true }}
            >
                <Background
                    variant={BackgroundVariant.Dots}
                    gap={16}
                    size={1}
                    color={isDark ? '#374151' : '#e5e7eb'}
                />
                <Controls
                    className={isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
                />
                <Panel position="top-right" className="flex gap-2">
                    <button
                        onClick={() => onLayout('TB')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            isDark
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Vertical
                    </button>
                    <button
                        onClick={() => onLayout('LR')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            isDark
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Horizontal
                    </button>
                </Panel>
            </ReactFlow>
        </div>
    );
};

// Wrapper component with ReactFlowProvider
const OrganizationChart: React.FC<OrganizationChartProps> = (props) => {
    return (
        <ReactFlowProvider>
            <OrganizationChartInner {...props} />
        </ReactFlowProvider>
    );
};

export default OrganizationChart;
